import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import cx from 'classnames'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AvatarRound, BtnV2, Icon, Loader, l } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { useSelectedGoalData } from 'apps/aiAutomation/components/EngagementGoalEditor/hooks/useSelectedGoalData'
import {
  SubmitRef,
  SubmitResult,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationEngagementGoalSelectors } from 'apps/aiAutomation/store/engagementGoal'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { getInstagramChannel } from 'common/core/selectors/channelSelectors'
import { HookFormTextArea } from 'components/HookFormControls/HookFormTextArea/HookFormTextArea'

import sharedCm from '../../../EditorLayout/styles.module.css'
import { MessageEditorFormValues } from '../../lib/types'

import cm from './styles.module.css'

interface MessageEditorProps {
  messageData: MessageEditorFormValues | null
  isActive: boolean
  isPublishBlocked: boolean
  onFinish: (data: MessageEditorFormValues) => void
}

export const MessageEditor = forwardRef<SubmitRef<MessageEditorFormValues>, MessageEditorProps>(
  ({ messageData, isActive, isPublishBlocked, onFinish }, ref) => {
    const dispatch = useAppDispatch()
    const { selectedGoal } = useSelectedGoalData()

    const { avatar } = useAppSelector(getInstagramChannel)

    const isLoading = useAppSelector((state) =>
      selectedGoal
        ? AiAutomationEngagementGoalSelectors.getIsItemProcessingAction(state, selectedGoal.id)
        : AiAutomationEngagementGoalSelectors.getIsNewItemCreating(state),
    )

    const { formState, handleSubmit, reset, control } = useForm({
      defaultValues: {
        message:
          messageData?.message ??
          l.translate('Hey, glad to have you here, follow along if you’re curious! 👋'),
      },
      resolver: zodResolver(
        z.object({
          message: z
            .string()
            .min(1, l.translate('Enter message to continue'))
            .max(1000, l.translate('Message must be {count} characters or fewer', { count: 1000 })),
        }),
      ),
    })

    const onSubmit = useCallback(
      (data: MessageEditorFormValues) => {
        if (isLoading) {
          return
        }

        reset(data)
        onFinish(data)
      },
      [isLoading, onFinish, reset],
    )

    useEffect(() => {
      if (isActive) {
        dispatch(AiAutomationLayoutActions.confirmChanged({ show: formState.isDirty }))
      } else {
        dispatch(AiAutomationLayoutActions.confirmChanged({ show: false }))
      }
    }, [dispatch, formState.isDirty, isActive])

    const formId = useId()

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          let result: SubmitResult<MessageEditorFormValues> = { isValid: false }

          await handleSubmit((data) => (result = { isValid: true, data }))()

          return result
        },
      }),
      [handleSubmit],
    )

    return (
      <section>
        <div className={sharedCm.section}>
          <h4 className={cx('font-medium', !isActive && 'text-secondary')}>
            {l.translate('Customize the message AI will send')}
          </h4>

          <div
            className="p-relative"
            // @ts-expect-error type for inert is supported starting from React 19+
            // It's also not working for now when we pass true/false, that's why there is an empty string and undefined values
            inert={isLoading ? '' : undefined}
          >
            <p className="m-t-xxs m-b-md text-secondary">
              {l.translate('Your edits will improve AI over time')}

              <Icon.Question
                data-title={l.translate(
                  'AI gets smarter from your edits, capturing your style to write replies that match your tone more effectively.',
                )}
                size={16}
                data-title-at="left"
                className={cm.questionIcon}
              />
            </p>

            <form id={formId} onSubmit={handleSubmit(onSubmit)}>
              <div className={cm.messageInputWrapper}>
                <HookFormTextArea
                  ariaLabel={l.translate('<l10n context="Noun"/>Message')}
                  placeholder={l.translate(
                    'Hey, glad to have you here, follow along if you’re curious! 👋',
                  )}
                  minRows={2}
                  maxRows={6}
                  disabled={!isActive}
                  control={control}
                  name="message"
                />
                <AvatarRound aria-hidden className={cm.avatar} src={avatar} />
              </div>
            </form>

            {isLoading && (
              <div className={cm.loader}>
                <Loader className={cm.loader__spinner} size={16} />

                <p className="text-secondary">
                  {l.translate('AI is writing based on your input…')}
                </p>
              </div>
            )}
          </div>
        </div>

        {isActive && !messageData && (
          <div className="m-t-sm d-flex justify-end">
            <BtnV2 disabled={isLoading} form={formId} type="submit">
              {isPublishBlocked ? l.translate('Save') : l.translate('Next')}
            </BtnV2>
          </div>
        )}
      </section>
    )
  },
)
